/**
 * Join by 4-letter code (SPEC §5.1) — the path for whoever's camera is broken.
 *
 * The alphabet already excludes ambiguous glyphs (no I/1, no O/0), so the input
 * can simply discard anything outside it as you type: paste a code with spaces,
 * type lower-case, it still works.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROOM_CODE_LENGTH } from '@idgame/protocol';
import { isValidRoomCode, sanitiseRoomCodeInput } from '../../lib/roomCode.js';
import { Button, Screen } from '../../app/ui.js';

export function JoinCodeScreen() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const ready = isValidRoomCode(code);
  const slots = Array.from({ length: ROOM_CODE_LENGTH }, (_, index) => code[index] ?? '');

  const submit = () => {
    if (ready) navigate(`/r/${code}`);
  };

  return (
    <Screen
      eyebrow="Join"
      title="Room code."
      sub="Four letters, on the host's phone."
      testId="join-code"
      footer={
        <div className="flex flex-col gap-2.5">
          <Button form="join-code" type="submit" disabled={!ready}>
            Join
          </Button>
          <Button variant="quiet" onClick={() => navigate('/')}>
            Back
          </Button>
        </div>
      }
    >
      <form
        id="join-code"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="relative block">
          <span className="sr-only">Room code</span>
          <input
            ref={inputRef}
            value={code}
            onChange={(event) => setCode(sanitiseRoomCodeInput(event.target.value))}
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            maxLength={ROOM_CODE_LENGTH}
            aria-label="Room code"
            className="absolute inset-0 h-full w-full opacity-0"
          />
          <div aria-hidden="true" className="grid grid-cols-4 gap-3">
            {slots.map((char, index) => (
              <div
                key={index}
                className={[
                  'rounded-card flex h-[4.5rem] items-center justify-center border text-3xl font-semibold',
                  char
                    ? 'border-2 border-terracotta bg-cream text-ink'
                    : 'border-line bg-cream-deep text-ink-soft/45',
                ].join(' ')}
              >
                {char || '·'}
              </div>
            ))}
          </div>
        </label>
      </form>
      <div className="flex-1" />
    </Screen>
  );
}
